/**
 * EvidenceReport — Reporte de evidencias de una ejecución.
 *
 * Muestra el detalle completo paso a paso de un run:
 * - Portada con KPIs del plan
 * - Por cada caso: título, tags, estado
 * - Por cada paso: descripción, resultado esperado, resultado real,
 *   estado, duración, screenshot (si existe), error (si falló)
 * - Soporte para impresión/PDF via window.print()
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { executions as executionsApi, defectsApi } from '../../services/api';
import { useAppStore } from '../../store';
import type { ExecutionPlan, ExecutionRun } from '../../types';

// ─── Types ──────────────────────────────────────────────────────────────────

interface StepResultDetail {
  id: string;
  step_id: string;
  step_order: number;
  step_description: string;
  step_expected_result: string | null;
  status: 'passed' | 'failed' | 'skipped' | 'error';
  error_message: string | null;
  screenshot_ref: { url?: string; thumbnail_url?: string; captured_at?: string } | null;
  duration_ms: number | null;
  healing_applied: { applied?: boolean; original_selector?: string; new_selector?: string; confidence?: number } | null;
  executed_at: string | null;
  attempt_number: number;
  extracted_value: string | null;
}

interface RunEvidence {
  run_id: string;
  case_id: string;
  case_title: string;
  case_tags: string[];
  scenario_title: string | null;
  requirement_title: string | null;
  status: string;
  total_steps: number;
  passed_steps: number;
  failed_steps: number;
  skipped_steps: number;
  duration_ms: number | null;
  error_summary: string | null;
  started_at: string | null;
  finished_at: string | null;
  step_results: StepResultDetail[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(ms?: number | null) {
  if (!ms) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function passRate(passed: number, total: number) {
  if (!total) return '—';
  return `${Math.round(passed / total * 100)}%`;
}

const STATUS_CFG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  passed:  { label: 'Passed',  color: '#0F6E56', bg: '#E1F5EE', icon: '✓' },
  failed:  { label: 'Failed',  color: '#A32D2D', bg: '#FCEBEB', icon: '✗' },
  skipped: { label: 'Skipped', color: '#854F0B', bg: '#FAEEDA', icon: '⊘' },
  error:   { label: 'Error',   color: '#793C1D', bg: '#FAECE7', icon: '!' },
  running: { label: 'Running', color: '#185FA5', bg: '#E6F1FB', icon: '◉' },
  pending: { label: 'Pending', color: '#5F5E5A', bg: '#F1EFE8', icon: '○' },
};

// ─── Print CSS ────────────────────────────────────────────────────────────────

const PRINT_STYLE = `
@media print {
  @page { margin: 15mm 12mm; size: A4; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #111; }
  .no-print { display: none !important; }
  .page-break { page-break-before: always; }
  .avoid-break { page-break-inside: avoid; }
  .report-cover { page-break-after: always; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 0.5pt solid #ccc; padding: 5px 8px; }
  .screenshot-img { max-width: 100%; max-height: 200px; border: 0.5pt solid #ccc; }
}
@media screen {
  .report-wrapper { background: #f5f5f5; padding: 24px; }
  .report-page { background: white; max-width: 900px; margin: 0 auto 24px; padding: 32px; border-radius: 8px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); }
}
`;

// ─── Report Components ────────────────────────────────────────────────────────

function ReportCover({ plan, runs }: {
  plan: ExecutionPlan;
  runs: RunEvidence[];
}) {
  const totalCases = runs.length;
  const passedCases = runs.filter(r => r.status === 'passed').length;
  const failedCases = runs.filter(r => r.status === 'failed').length;
  const totalSteps = runs.reduce((a, r) => a + r.total_steps, 0);
  const passedSteps = runs.reduce((a, r) => a + r.passed_steps, 0);
  const totalDuration = runs.reduce((a, r) => a + (r.duration_ms ?? 0), 0);

  // Fecha real de la ejecución (no de generación del reporte): la más temprana
  // entre los runs mostrados, para que coincida con cuando corrieron los casos.
  const executionTimestamps = runs
    .map(r => r.started_at ?? r.finished_at)
    .filter((d): d is string => !!d)
    .map(d => new Date(d).getTime());
  const executionDate = executionTimestamps.length
    ? formatDate(new Date(Math.min(...executionTimestamps)).toISOString())
    : '—';

  return (
    <div className="report-page report-cover">
      {/* Header */}
      <div style={{ borderBottom: '3px solid #1B2E5E', paddingBottom: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
              Reporte de Evidencias de Ejecución
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#1B2E5E', lineHeight: 1.2 }}>
              {plan.name}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#666' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#F47920' }}>BASSP</div>
            <div>QA Platform</div>
          </div>
        </div>
      </div>

      {/* Metadata grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginBottom: 28, fontSize: 12 }}>
        {[
          { label: 'Ambiente', value: plan.environment },
          { label: 'Browser', value: (plan.config as Record<string,string>)?.browser ?? '—' },
          { label: 'Fecha de ejecución', value: executionDate },
          { label: 'Duración total', value: formatDuration(totalDuration) },
        ].map(row => (
          <div key={row.label} style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#666', minWidth: 100 }}>{row.label}:</span>
            <span style={{ fontWeight: 600, color: '#111' }}>{row.value}</span>
          </div>
        ))}
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
        {[
          { label: 'Total casos', value: totalCases, color: '#1B2E5E' },
          { label: 'Pasaron',     value: passedCases, color: '#0F6E56' },
          { label: 'Fallaron',    value: failedCases, color: '#A32D2D' },
          { label: 'Pass rate',   value: passRate(passedCases, totalCases), color: passedCases === totalCases ? '#0F6E56' : '#A32D2D' },
        ].map(k => (
          <div key={k.label} style={{ padding: '14px 12px', border: '1px solid #e0e0e0', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Steps summary */}
      <div style={{ padding: '12px 16px', background: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0', marginBottom: 28 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 8 }}>Resumen de pasos</div>
        <div style={{ display: 'flex', gap: 24, fontSize: 12 }}>
          <span><strong style={{ color: '#0F6E56' }}>{passedSteps}</strong> pasaron</span>
          <span><strong style={{ color: '#A32D2D' }}>{totalSteps - passedSteps}</strong> fallaron/omitieron</span>
          <span><strong>{totalSteps}</strong> total</span>
          <span><strong>{passRate(passedSteps, totalSteps)}</strong> paso a paso</span>
        </div>
        {/* Progress bar */}
        <div style={{ marginTop: 8, height: 8, borderRadius: 4, background: '#e0e0e0', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${totalSteps ? passedSteps / totalSteps * 100 : 0}%`, background: '#1D9E75' }} />
        </div>
      </div>

      {/* Cases index */}
      <div style={{ fontSize: 12 }}>
        <div style={{ fontWeight: 700, color: '#333', marginBottom: 10, fontSize: 13 }}>Índice de casos</div>
        <table>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ width: 30, textAlign: 'center' }}>#</th>
              <th>Caso de prueba</th>
              <th style={{ width: 80 }}>Escenario</th>
              <th style={{ width: 60, textAlign: 'center' }}>Estado</th>
              <th style={{ width: 60, textAlign: 'center' }}>Pasos</th>
              <th style={{ width: 70, textAlign: 'center' }}>Duración</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run, i) => {
              const sc = STATUS_CFG[run.status] ?? STATUS_CFG.pending;
              return (
                <tr key={run.run_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ textAlign: 'center', color: '#666' }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{run.case_title}</td>
                  <td style={{ color: '#666', fontSize: 10 }}>{run.scenario_title ?? '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ padding: '2px 6px', borderRadius: 4, background: sc.bg, color: sc.color, fontSize: 10, fontWeight: 700 }}>
                      {sc.icon} {sc.label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', color: '#666' }}>{run.passed_steps}/{run.total_steps}</td>
                  <td style={{ textAlign: 'center', color: '#666' }}>{formatDuration(run.duration_ms)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunReport({ run, index }: { run: RunEvidence; index: number }) {
  const sc = STATUS_CFG[run.status] ?? STATUS_CFG.pending;
  const isFirst = index === 0;
  const [creatingDefect, setCreatingDefect] = useState(false);
  const [defectCreated, setDefectCreated] = useState(false);
  const setSection = useAppStore(s => s.setSection);

  const handleCreateDefect = async () => {
    setCreatingDefect(true);
    try {
      await defectsApi.create({
        title: `Fallo en: ${run.case_title}`,
        error_summary: run.error_summary || undefined,
        steps_to_reproduce: run.step_results
          .filter(sr => sr.status === 'failed' || sr.status === 'error')
          .map(sr => `Paso: ${sr.step_description}\nError: ${sr.error_message || 'Sin detalle'}`)
          .join('\n\n'),
        environment: undefined,
        severity: 'high',
        execution_run_id: run.run_id,
        test_case_id: run.case_id,
        test_case_title: run.case_title,
        screenshot_refs: run.step_results
          .filter(sr => sr.screenshot_ref?.url)
          .map(sr => ({ url: sr.screenshot_ref!.url, thumbnail_url: sr.screenshot_ref!.thumbnail_url })),
      });
      setDefectCreated(true);
    } catch (e) {
      console.error('Error creando defecto:', e);
    } finally {
      setCreatingDefect(false);
    }
  };

  return (
    <div className={`report-page ${isFirst ? '' : 'page-break'}`}>
      {/* Case header */}
      <div style={{ borderBottom: '2px solid #1B2E5E', paddingBottom: 14, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: '#888', marginBottom: 3 }}>
              {run.requirement_title && `${run.requirement_title} › `}{run.scenario_title}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1B2E5E' }}>
              Caso {index + 1}: {run.case_title}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {run.case_tags.map(t => (
                <span key={t} style={{ padding: '1px 7px', borderRadius: 4, background: '#E6F1FB', color: '#185FA5', fontSize: 9, fontWeight: 700 }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <span style={{ padding: '4px 12px', borderRadius: 6, background: sc.bg, color: sc.color, fontSize: 13, fontWeight: 700 }}>
              {sc.icon} {sc.label}
            </span>
            <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>{formatDuration(run.duration_ms)}</div>
            {(run.status === 'failed' || run.status === 'error') && !defectCreated && (
              <button onClick={handleCreateDefect} disabled={creatingDefect}
                className='no-print'
                style={{ marginTop: 8, padding: '5px 10px', borderRadius: 6, border: '1px solid #C53030',
                  background: creatingDefect ? '#F7C1C1' : '#FCEBEB', color: '#7A1515',
                  fontSize: 11, fontWeight: 600, cursor: creatingDefect ? 'not-allowed' : 'pointer' }}>
                {creatingDefect ? 'Creando...' : '🐛 Crear defecto'}
              </button>
            )}
            {defectCreated && (
              <div className='no-print' style={{ marginTop: 8, fontSize: 11, color: '#0F6E56', fontWeight: 600 }}>
                ✓ Defecto creado
                <button onClick={() => (setSection as (s: string) => void)('defects')}
                  style={{ marginLeft: 6, border: 'none', background: 'transparent', color: '#185FA5', fontSize: 11, cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                  Ver defectos →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Run metadata */}
        <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 11, color: '#666' }}>
          <span>Inicio: {formatDate(run.started_at)}</span>
          <span>Fin: {formatDate(run.finished_at)}</span>
          <span>{run.passed_steps} ✓ / {run.failed_steps} ✗ / {run.total_steps} total</span>
        </div>

        {run.error_summary && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: '#FCEBEB', border: '1px solid #F7C1C1', borderRadius: 6, fontSize: 11, color: '#791F1F' }}>
            ⚠ {run.error_summary}
          </div>
        )}
      </div>

      {/* Steps */}
      {run.step_results.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: 12, border: '1px dashed #ccc', borderRadius: 8 }}>
          Sin resultados de pasos registrados
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {run.step_results.map((sr, i) => {
            const ssc = STATUS_CFG[sr.status] ?? STATUS_CFG.pending;
            const hasHealing = sr.healing_applied?.applied;
            const hasScreenshot = !!sr.screenshot_ref?.url;

            return (
              <div key={sr.id} className="avoid-break" style={{ border: `1px solid ${sr.status === 'failed' || sr.status === 'error' ? '#F7C1C1' : '#e0e0e0'}`, borderRadius: 8, overflow: 'hidden' }}>
                {/* Step header */}
                <div style={{ padding: '10px 14px', background: sr.status === 'failed' || sr.status === 'error' ? '#FCEBEB' : sr.status === 'passed' ? '#E1F5EE22' : '#f9f9f9', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: ssc.bg, color: ssc.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {ssc.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: '#888' }}>Paso {sr.step_order}</span>
                      <span style={{ padding: '1px 6px', borderRadius: 4, background: ssc.bg, color: ssc.color, fontSize: 10, fontWeight: 700 }}>
                        {ssc.label}
                      </span>
                      {sr.attempt_number > 1 && (
                        <span style={{ padding: '1px 6px', borderRadius: 4, background: '#FAEEDA', color: '#854F0B', fontSize: 10, fontWeight: 700 }}>
                          Intento #{sr.attempt_number}
                        </span>
                      )}
                      {hasHealing && (
                        <span style={{ padding: '1px 6px', borderRadius: 4, background: '#EEEDFE', color: '#534AB7', fontSize: 10, fontWeight: 700 }}>
                          ✦ Self-healing aplicado
                        </span>
                      )}
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: '#888' }}>{formatDuration(sr.duration_ms)}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1B2E5E', marginTop: 4 }}>
                      {sr.step_description}
                    </div>
                  </div>
                </div>

                {/* Step detail */}
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Expected result */}
                  {sr.step_expected_result && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                        Resultado esperado
                      </div>
                      <div style={{ fontSize: 12, color: '#333', padding: '6px 10px', background: '#f5f9ff', borderLeft: '3px solid #3B8BD4', borderRadius: '0 4px 4px 0' }}>
                        {sr.step_expected_result}
                      </div>
                    </div>
                  )}

                  {/* Valor extraído por UI_EXTRACT */}
                  {sr.extracted_value != null && sr.extracted_value !== '' && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#0F6E56', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                        Valor extraído
                      </div>
                      <div style={{ fontSize: 12, color: '#0B5C45', padding: '6px 10px', background: '#E1F5EE', borderLeft: '3px solid #1D9E75', borderRadius: '0 4px 4px 0', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {sr.extracted_value}
                      </div>
                    </div>
                  )}

                  {/* Actual result / error */}
                  {sr.error_message && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#A32D2D', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                        Error encontrado
                      </div>
                      <div style={{ fontSize: 12, color: '#791F1F', padding: '6px 10px', background: '#FCEBEB', borderLeft: '3px solid #E24B4A', borderRadius: '0 4px 4px 0', fontFamily: 'monospace' }}>
                        {sr.error_message}
                      </div>
                    </div>
                  )}

                  {/* Self-healing detail */}
                  {hasHealing && sr.healing_applied && (
                    <div style={{ padding: '8px 12px', background: '#EEEDFE', borderRadius: 6, fontSize: 11 }}>
                      <div style={{ fontWeight: 700, color: '#534AB7', marginBottom: 4 }}>✦ Self-healing aplicado</div>
                      <div style={{ color: '#3C3489' }}>
                        <div>Selector original: <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{sr.healing_applied.original_selector}</code></div>
                        <div>Nuevo selector: <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{sr.healing_applied.new_selector}</code></div>
                        {sr.healing_applied.confidence && (
                          <div>Confianza: {Math.round(sr.healing_applied.confidence * 100)}%</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Screenshot */}
                  {hasScreenshot && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                        Captura de pantalla
                        {sr.screenshot_ref?.captured_at && (
                          <span style={{ fontWeight: 400, marginLeft: 6 }}>— {formatDate(sr.screenshot_ref.captured_at)}</span>
                        )}
                      </div>
                      <img
                        src={sr.screenshot_ref!.thumbnail_url ?? sr.screenshot_ref!.url}
                        alt={`Screenshot paso ${sr.step_order}`}
                        className="screenshot-img"
                        style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 6, display: 'block' }}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  )}

                  {/* Execution time */}
                  <div style={{ fontSize: 10, color: '#aaa', textAlign: 'right' }}>
                    Ejecutado: {formatDate(sr.executed_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface EvidenceReportProps {
  plan: ExecutionPlan;
  onClose: () => void;
  /** Runs ya cargados de un intento puntual (p.ej. desde PlanHistoryPanel). Si
   * se omiten, se cargan los runs del intento vigente vía getRuns(plan.id). */
  runs?: ExecutionRun[];
  /** Número de intento mostrado, solo para el título. */
  attempt?: number;
}

export function EvidenceReport({ plan, onClose, runs: runsProp, attempt }: EvidenceReportProps) {
  const [evidence, setEvidence] = useState<RunEvidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState(false);
  const generatedAt = new Date().toLocaleString('es', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const runs = runsProp ?? await executionsApi.getRuns(plan.id);
        const results = await Promise.all(
          runs.map(r => executionsApi.getRunStepResults(r.id).then(data => data as unknown as RunEvidence))
        );
        setEvidence(results);
      } catch (e: unknown) {
        setError((e as Error).message ?? 'Error cargando evidencias');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [plan.id, runsProp]);

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 300);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: '#f0f0f0', overflowY: 'auto' }}>
      <style>{PRINT_STYLE}</style>

      {/* Toolbar */}
      <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#1B2E5E', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
            Reporte de evidencias — {plan.name}{attempt != null ? ` · Ejecución #${attempt}` : ''}
          </div>
          <div style={{ fontSize: 11, color: '#B5D4F4' }}>{evidence.length} casos · Generado {generatedAt}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {/* Botón de impresión deshabilitado: el diálogo nativo del navegador
              no preserva formato ni calidad. Pendiente solución server-side.
          <button
            onClick={handlePrint}
            disabled={loading || printing}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#F47920', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: loading ? 0.6 : 1 }}
          >
            {printing ? '⟳ Preparando...' : '🖨 Imprimir / Guardar PDF'}
          </button>
          */}
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
            ✕ Cerrar
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="report-wrapper">
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⟳</div>
            <div>Cargando evidencias...</div>
          </div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#A32D2D' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⚠</div>
            <div>{error}</div>
          </div>
        ) : (
          <>
            <ReportCover plan={plan} runs={evidence} />
            {evidence.map((run, i) => (
              <RunReport key={run.run_id} run={run} index={i} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}